import { api } from './api';

export interface Message {
  _id: string;
  conversationId: string;
  senderId: string | {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  receiverId: string | {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  schoolId: string | {
    _id: string;
    name: string;
  };
  bookingId?: string;
  content: string;
  status?: 'PENDING' | 'SENT' | 'DELETED';
  sentAt?: string;
  deletedAt?: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Conversation {
  conversationId: string;
  school: {
    _id: string;
    name: string;
    slug?: string;
    logo?: string;
  } | null;
  otherUser: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  lastMessage: Message | null;
  unreadCount: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ConversationsResponse {
  success: boolean;
  data: {
    conversations: Conversation[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface MessagesResponse {
  success: boolean;
  data: {
    messages: Message[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface UnreadCountResponse {
  success: boolean;
  data: {
    unreadCount: number;
  };
}

export const messageService = {
  async sendMessage(schoolId: string, content: string, receiverId?: string, bookingId?: string): Promise<ApiResponse<Message>> {
    const payload: any = {
      schoolId,
      content,
    };
    if (receiverId) {
      payload.receiverId = receiverId;
    }
    if (bookingId) {
      payload.bookingId = bookingId;
    }
    const response = await api.post<ApiResponse<Message>>('/messages', payload);
    return response.data;
  },

  async getConversations(page: number = 1, limit: number = 20): Promise<ConversationsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await api.get<ConversationsResponse>(`/messages/conversations?${params.toString()}`);
    return response.data;
  },

  async getMessages(conversationId?: string, schoolId?: string, bookingId?: string, page: number = 1, limit: number = 50): Promise<MessagesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (conversationId) {
      params.append('conversationId', conversationId);
    }
    if (schoolId) {
      params.append('schoolId', schoolId);
    }
    if (bookingId) {
      params.append('bookingId', bookingId);
    }
    const response = await api.get<MessagesResponse>(`/messages?${params.toString()}`);
    return response.data;
  },

  async markAsRead(messageIds: string[]): Promise<ApiResponse<{ updatedCount: number }>> {
    const response = await api.patch<ApiResponse<{ updatedCount: number }>>('/messages/read', {
      messageIds,
    });
    return response.data;
  },

  async getUnreadCount(): Promise<UnreadCountResponse> {
    const response = await api.get<UnreadCountResponse>('/messages/unread-count');
    return response.data;
  },

  async deleteMessage(messageId: string): Promise<ApiResponse<Message>> {
    const response = await api.delete<ApiResponse<Message>>(`/messages/${messageId}`);
    return response.data;
  },
};

